/* eslint-disable */
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import {
  useInvalidateQueries,
} from 'src/api/APIHooks';
import { screeningsApiClient } from 'src/api/config';
import today from 'common/utils/today';
import Notifier from 'common/helpers/Notifier';
import { browserHistory } from 'common/utils/browserHistory';
import { SurveyJSFormRenderer, buildResponses } from '@unite-us/ui-surveyjs2';
import {
  BaseCard,
  BaseCardBody,
} from '@unite-us/ui';
import {
  useElAssessment,
  EL_ASSESSMENT_QUERY_KEYS
} from 'src/pages/facesheet/_id/eligibility/hooks/useEligibilityAssessments';
import './EligibilityAssessment.scss';
import {
  updateAssessment,
  createNewAssessment,
  useElAssessmentTemplateId,
  useTemplateV2,
  useMyScreenings,
} from 'src/pages/facesheet/_id/eligibility/hooks/eligibilityApi';
import ContactTitle from './components/ContactTitle';
import {
  useRelatedScreening,
  renderIdentifiedNeeds,
} from 'src/pages/facesheet/_id/eligibility/hooks/useRelatedScreening';
import {
  setOptionsForScreeningDropdown,
} from 'src/pages/facesheet/_id/eligibility/utils/relatedScreeningUtils';
import {useFeatureFlag} from "../../../../common/hooks";

const SURVEY_PAGE_NUMBER = 0; // set to 0 for page1, 1 for page2, etc.

export const buildAnswersPayload = (surveyRef, surveyJSON) => {
  const survey = surveyRef.current;

  const responses = buildResponses(surveyRef.current);
  const answers = responses
    .map((response) => {
      let option_id = null;
      const elementName = response.name;
      if (['qDateAttempt1', 'qDateAttempt2', 'qDateAttempt3'].includes(elementName)) {
        return {
          question_id: response.id,
          value: response.value ? moment(response.value)
            .set({ hour: 8, minute: 0, second: 0 })
            .format('YYYY-MM-DDTHH:mm:ss[Z]') : null,
        };
      }

      if (elementName === 'qSelectScreening') {
        return {
          question_id: response.id,
          value: response.value,
        };
      }

      const optionsSelected = response.custom.map((c) => c.option.uuid || c.option.id);
      const questionType = get(response, 'custom[0].question.type', '');
      option_id = optionsSelected;
      if (response.value) {
        option_id = response.value;
      } else if (questionType === 'select_multiple') {
        option_id = Array.isArray(optionsSelected) ? optionsSelected : [optionsSelected];
      } else if (optionsSelected.length > 1) {
        option_id = optionsSelected;
      } else {
        option_id = optionsSelected[0];
      }
      return {
        question_id: response.id,
        option_id: response.valueId[0],
      }
    });

  return answers;
};

const getOutreachValues = (surveyRef) => {
  const ASSESSED_ANSWER = 'Success';
  const DECLINED_ANSWER = 'No response';
  const WRONG_INFO = 'Wrong info';
  const survey = surveyRef.current.pages[0];
  const qDateAttempt1Value = survey.getQuestionByName('qDateAttempt1').value;
  const qDateAttempt2Value = survey.getQuestionByName('qDateAttempt2').value;
  const qDateAttempt3Value = survey.getQuestionByName('qDateAttempt3').value;

  const qOutcomeAttempt1Value = survey.getQuestionByName('qOutcomeAttempt1').value;
  const qModalityAttempt2Value = survey.getQuestionByName('qModalityAttempt2').value;
  const qModalityAttempt3Value = survey.getQuestionByName('qModalityAttempt3').value;

  const qModalityAttempt1Value = survey.getQuestionByName('qModalityAttempt1').value;
  const qOutcomeAttempt2Value = survey.getQuestionByName('qOutcomeAttempt2').value;
  const qOutcomeAttempt3Value = survey.getQuestionByName('qOutcomeAttempt3').value;

  const qSelectScreening = survey.getQuestionByName('qSelectScreening');
  const hasSelectedScreening = Boolean(qSelectScreening.value);

  const patientHasAssessed = (
    qDateAttempt1Value && qModalityAttempt1Value && qOutcomeAttempt1Value === ASSESSED_ANSWER
  ) || (
    qDateAttempt2Value && qModalityAttempt2Value && qOutcomeAttempt2Value === ASSESSED_ANSWER
  ) || (
    qDateAttempt3Value && qModalityAttempt3Value && qOutcomeAttempt3Value === ASSESSED_ANSWER
  );
  const patientHasDeclined = (
    qDateAttempt1Value && qModalityAttempt1Value &&
      (qOutcomeAttempt1Value === DECLINED_ANSWER || qOutcomeAttempt1Value === WRONG_INFO)
  ) && (
    qDateAttempt2Value && qModalityAttempt2Value &&
      (qOutcomeAttempt2Value === DECLINED_ANSWER || qOutcomeAttempt2Value === WRONG_INFO)
  ) && (
    qDateAttempt3Value && qModalityAttempt3Value &&
    (qOutcomeAttempt3Value === DECLINED_ANSWER || qOutcomeAttempt3Value === WRONG_INFO)
  );

  return {
    isOutreachOneNegative: qDateAttempt1Value && qModalityAttempt1Value && (
      qOutcomeAttempt1Value === DECLINED_ANSWER || qOutcomeAttempt1Value === WRONG_INFO
    ),
    isOutreachTwoNegative: qDateAttempt2Value && qModalityAttempt2Value && (
      qOutcomeAttempt2Value === DECLINED_ANSWER || qOutcomeAttempt2Value === WRONG_INFO
    ),
    patientHasAssessed: Boolean(patientHasAssessed),
    patientHasDeclined: Boolean(patientHasDeclined),
    hasSelectedScreening,
  }
};

const populateQuestion = (
  surveyRef,
  assessment,
  questionName,
  questionType,
) => {
  const surveyPage = surveyRef.current.pages[0];
  const isFormEditable = !get(assessment, 'data.screen.outreach_status');
  const screenQuestions = get(assessment, 'data.screen.questions', []);
  let element = surveyPage.getQuestionByName(questionName);
  let screenQuestion;
  let elementAnswer;
  if (questionType === 'dropdown') {
    screenQuestion = screenQuestions.find(
      (q) => q.primary_text.toLowerCase() === element.title.toLowerCase()
    );
    element.readOnly = !isFormEditable;
    element.value = get(screenQuestion, 'answer.value', null);
    element.visible = true;
    if (!isFormEditable) {
      element.inputType = 'text';
    }
  }

  if (questionType === 'dropdown-multiselect') {
    screenQuestion = screenQuestions.find(
      (q) => q.primary_text.toLowerCase() === element.title.toLowerCase()
    );
    if (get(screenQuestion, 'answers', []).length > 0) {
      const selectedValues = screenQuestion.answers.map((a) => a.value);
      element.readOnly = true;
      elementAnswer = selectedValues;
      element.value = selectedValues;
    }
    element.visible = true;
  }

  if (questionType === 'date') {
    screenQuestion = screenQuestions.find(
      (q) => q.primary_text.toLowerCase() === element.title.toLowerCase()
    );
    if (get(screenQuestion, 'answer.value')) {
      element.readOnly = !isFormEditable;
      element.value = moment(screenQuestion.answer.value).toDate();
    }
    element.visible = true;
  }

  return {
    element,
    screenQuestion,
    elementAnswer,
  };
};

const EligibilityOutreach = (props) => {
  const {
    employeeId,
    personId,
    setLoading,
    contactData,
    onContactClick,
  } = props;
  const surveyRef = useRef(null);
  const [relatedScreenId, setRelatedScreenId] = useState(null);
  const { relatedScreening } = useRelatedScreening(relatedScreenId);
  const [ready, setReady] = useState(false);
  const templateId = useElAssessmentTemplateId();
  const paramAssessmentId = props.screenId;
  const inEditMode = Boolean(paramAssessmentId);

  const { data: assessment } = useElAssessment(
    paramAssessmentId,
    { enabled: Boolean(paramAssessmentId) }
  );
  const [refReady, setRefReady] = useState(null);
  const invalidateQueries = useInvalidateQueries();

  const { data: templateData } = useTemplateV2({ templateId });
  const { data: screenings } = useMyScreenings(personId);
  let formJSON = get(templateData, 'surveyjsTemplate');
  if (formJSON) {
    formJSON = JSON.parse(JSON.stringify(formJSON));
  }
  
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isOutreachButtonDisabled, setIsOutreachButtonDisabled] = useState(true);

  const populateForm = (surveyRef, assessment) => {
    populateQuestion(surveyRef, assessment, 'qSelectScreening', 'dropdown', relatedScreening);
    populateQuestion(surveyRef, assessment, 'qDateAttempt1', 'date');
    populateQuestion(surveyRef, assessment, 'qModalityAttempt1', 'dropdown');
    populateQuestion(surveyRef, assessment, 'qOutcomeAttempt1', 'dropdown');
    populateQuestion(surveyRef, assessment, 'qDateAttempt2', 'date');
    populateQuestion(surveyRef, assessment, 'qModalityAttempt2', 'dropdown');
    populateQuestion(surveyRef, assessment, 'qOutcomeAttempt2', 'dropdown');
    populateQuestion(surveyRef, assessment, 'qDateAttempt3', 'date');
    populateQuestion(surveyRef, assessment, 'qModalityAttempt3', 'dropdown');
    populateQuestion(surveyRef, assessment, 'qOutcomeAttempt3', 'dropdown');
  };

  const handleSetSurvey = (survey) => {
    surveyRef.current = survey;
    surveyRef.current.currentPageNo = 0;
    setRefReady(surveyRef);
  };

  const invalidateCommonQueries = () => {
    invalidateQueries(EL_ASSESSMENT_QUERY_KEYS.ASSESSMENTS);
    invalidateQueries(EL_ASSESSMENT_QUERY_KEYS.ASSESSMENT);
  };

  const saveAsDraft = async () => {
    setLoading(true);

    const answers = buildAnswersPayload(surveyRef);
    const outreach_status = null;
    const related_screen_id = relatedScreenId || get(assessment, 'data.screen.related_screen_id', null);

    if (inEditMode) {
      const response = await updateAssessment({
        personId,
        employeeId,
        status: 'draft',
        answers,
        related_screen_id,
        outreach_status,
        templateId,
        assessmentId: paramAssessmentId,
      });

      Notifier.dispatch(200, 'Draft saved');
      invalidateCommonQueries();
      browserHistory.push(`/facesheet/${personId}/eligibility`);
      setLoading(false);
      return;
    }

    const response = await createNewAssessment({
      personId,
      employeeId,
      status: 'draft',
      answers,
      related_screen_id,
      outreach_status,
      templateId,
    });

    const newAssessmentId = get(response, 'data.screen.id');
    if (!newAssessmentId) {
      setLoading(false);
      return Notifier.dispatch(400, 'Failed to create new assessment');
    }
    Notifier.dispatch(200, 'Draft saved');
    invalidateCommonQueries();
    browserHistory.push(`/facesheet/${personId}/eligibility`);
    setLoading(false);
  };

  const failOutreach = async () => {
    setLoading(true);
    const answers = buildAnswersPayload(surveyRef);
    const outreach_status = 'declined';
    const status = 'complete';
    const related_screen_id = relatedScreenId || get(assessment, 'data.screen.related_screen_id', null);

    if (inEditMode) {
      invalidateQueries(EL_ASSESSMENT_QUERY_KEYS.ASSESSMENTS);
      await updateAssessment({
        personId,
        employeeId,
        status,
        answers,
        related_screen_id,
        outreach_status,
        templateId,
        assessmentId: paramAssessmentId,
      });
      invalidateCommonQueries();
      setLoading(false);
      return browserHistory.push(`/facesheet/${personId}/eligibility/all`);
    }

    await createNewAssessment({
      personId,
      employeeId,
      status,
      answers,
      related_screen_id,
      outreach_status,
      templateId,
    });
    invalidateCommonQueries();
    browserHistory.push(`/facesheet/${personId}/eligibility/all`);
    setLoading(false);
  };

  const submit = async () => {
    setLoading(true);
    const related_screen_id = relatedScreenId || get(assessment, 'data.screen.related_screen_id', null);

    const goToNextStep = (assessId) => {
      browserHistory.push(`/facesheet/${personId}/eligibility/new/${assessId}/consent`);
    };

    const hasOutreachSuccess = get(assessment, 'data.screen.outreach_status') === 'success';
    if (hasOutreachSuccess) {
      goToNextStep(paramAssessmentId);
      return setLoading(false);
    }

    const answers = buildAnswersPayload(surveyRef);
    if (inEditMode) {
      const response = await updateAssessment({
        personId,
        employeeId,
        status: 'draft',
        answers,
        related_screen_id,
        outreach_status: 'success',
        templateId,
        assessmentId: paramAssessmentId,
      });
      invalidateCommonQueries();
      goToNextStep(paramAssessmentId);
      return setLoading(false);
    }

    const response = await createNewAssessment({
      personId,
      employeeId,
      status: 'draft',
      answers,
      related_screen_id,
      outreach_status: 'success',
      templateId,
    });

    invalidateCommonQueries();
    const newAssessmentId = get(response, 'data.screen.id');
    setLoading(false);
    if (!newAssessmentId) {
      return Notifier.dispatch(400, 'Failed to create new assessment');
    }
    goToNextStep(newAssessmentId);
  };

  // index = 2 or 3
  const toggleOutreachAttempt = (index, display = false) => {
    const survey = surveyRef.current.pages[SURVEY_PAGE_NUMBER];
    const qDateAttempt = survey.getQuestionByName(`qDateAttempt${index}`);
    const qModalityAttempt = survey.getQuestionByName(`qModalityAttempt${index}`);
    const qOutcomeAttempt = survey.getQuestionByName(`qOutcomeAttempt${index}`);
    qDateAttempt.visible = display;
    qModalityAttempt.visible = display;
    qOutcomeAttempt.visible = display;
    return;
  };

  const evaluateFormVisibilities = () => {
    const {
      isOutreachOneNegative,
      isOutreachTwoNegative,
      patientHasAssessed,
      patientHasDeclined,
      hasSelectedScreening,
    } = getOutreachValues(surveyRef);

    toggleOutreachAttempt(2, Boolean(isOutreachOneNegative));
    toggleOutreachAttempt(3, Boolean(isOutreachTwoNegative));

    setIsSubmitDisabled(!patientHasAssessed || !hasSelectedScreening);
    setIsOutreachButtonDisabled(!patientHasDeclined || !hasSelectedScreening);
  };

  const onValueChanged = (sender, options, checkAll = false) => {
    evaluateFormVisibilities();
    const allElements = surveyRef.current.pages[SURVEY_PAGE_NUMBER].elements;

    const elementName = checkAll ? null : options.name;
    if (elementName === 'qSelectScreening') {
      const screeningId = options?.value;
      if (screeningId) {
        setRelatedScreenId(screeningId);
      }
    }

    allElements.forEach((question) => {
      if (!question.visible && question.value !== undefined) {
        question.clearValue();
      }
    });
  };

  const scrn942ScreeningsResultsUpdatesEnabled = useFeatureFlag('SCRN-942-screenings-results-updates');
  useEffect(() => {
    if (!relatedScreening && surveyRef?.current) { return; }
    renderIdentifiedNeeds(scrn942ScreeningsResultsUpdatesEnabled, surveyRef, relatedScreening);
  }, [relatedScreening, surveyRef?.current]);

  useEffect(() => {
    if (!surveyRef?.current || !screenings) { return; }
    setOptionsForScreeningDropdown(surveyRef, screenings);

    const qSelectScreening = surveyRef.current.getQuestionByName('qSelectScreening');
    const selectedScreenId = qSelectScreening?.value;
    const selectionStillValid = selectedScreenId && screenings.some((s) => s.id === selectedScreenId);

    if (!selectionStillValid) {
      qSelectScreening?.clearValue();
      setRelatedScreenId(null);
      evaluateFormVisibilities();
    }
  }, [screenings, surveyRef?.current]);

  useEffect(() => {
    if (!relatedScreenId || !surveyRef?.current) { return; }
    evaluateFormVisibilities();
  }, [relatedScreenId]);

  useEffect(() => {
    if (!assessment || !surveyRef?.current || !ready) { return; }
    populateForm(surveyRef, assessment);
    onValueChanged(null, null, true);
  }, [assessment, surveyRef?.current, ready]);

  useEffect(() => {
    if (assessment && !relatedScreenId) {
      const related_screen_id = get(assessment, 'data.screen.related_screen_id');
      setRelatedScreenId(related_screen_id);
    }
  }, [assessment]);

  if (!formJSON || (inEditMode && !assessment)) { return null; }
  if (!ready) { setReady(true) }

  formJSON.configuration.pages[SURVEY_PAGE_NUMBER].elements.forEach((element) => {
    if (element.name === 'qDateAttempt1' || element.name === 'qDateAttempt2' || element.name === 'qDateAttempt3') {
      element.custom.maxDate = today();
    }
    const isFormEditable = !get(assessment, 'data.screen.outreach_status');
    if (!isFormEditable && element.name === 'qSelectScreening') {
      element.readOnly = true;
      element.type = 'text';
    }
  });

  return (
    <div className="eligibility-assessment">
      <BaseCard className="bg-white">
        <div
          className='flex items-center justify-between border border-solid border-medium-border-blue border-l-0 border-r-0 border-t-0'
        >
          <div className="px-5 py-8 inline-block">
            <ContactTitle
              contactData={contactData}
              onContactClick={onContactClick}
              title="Eligibility Assessment for"
              />
            <p className="text-md">This form is to determine eligibility for HRSN services, if the client is not eligible, you can proceed to send referrals for regular client services.</p>
          </div>
        </div>

        <div className="px-6">
          <BaseCardBody>
            <SurveyJSFormRenderer
              formData={formJSON}
              formSubmission={null}
              editMode={true}
              onSurveyModelSet={handleSetSurvey}
              onValueChanged={onValueChanged}
            />

            <div
              className="flex justify-between items-center mb-6 mt-3"
            >
              <button
                onClick={saveAsDraft}
                className="h-12 mr-4 px-16 py-3 border border-solid border-light-border-blue rounded text-action-darker-blue">
                <span className="inline-block align-middle">Save as Draft</span>
              </button>

              <div className="flex space-x-4">
                <button
                  onClick={failOutreach}
                  disabled={isOutreachButtonDisabled}
                  style={{ backgroundColor: isOutreachButtonDisabled ? '#cbd5e0' : '#EF4444' }}
                  className={`h-12 px-16 py-3 border border-solid border-medium-border-blue rounded`}
                >
                  <span className="inline-block align-middle text-white">Outreach Failed (3 Attempts)</span>
                </button>
                
                <button
                  onClick={submit}
                  disabled={isSubmitDisabled}
                  style={{ backgroundColor: isSubmitDisabled ? '#cbd5e0' : '#0B61A4' }}
                  className={`h-12 px-16 py-3 rounded text-white`}
                >
                  <span className="inline-block align-middle text-white">
                    Continue
                  </span>
                </button>
              </div>

            </div>
          </BaseCardBody>
        </div>
      </BaseCard>
      <div style={{ display: "none" }}></div>
    </div>
  );
};

export default EligibilityOutreach;

EligibilityOutreach.propTypes = {
  employeeId: PropTypes.string.isRequired,
  personId: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
  screenId: PropTypes.string,
  answerConsent: PropTypes.func,
  contactData: PropTypes.object,
  onContactClick: PropTypes.func,
};
